import { type CSSProperties } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { cn } from '@sara/ui/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '@sara/ui/components/sidebar';
import { useDialog } from '@sara/ui/hooks/use-dialog';
import { useTheme } from '@sara/ui/hooks/use-theme';
import {
  AudioLines,
  Braces,
  Globe,
  Image,
  KeyRound,
  LayoutTemplate,
  Monitor,
  Moon,
  Plus,
  Shield,
  Sun,
  Target,
  Users,
  UsersRound,
  Webhook,
} from 'lucide-react';
import { useGetMe } from '@sara/api-client';
import { MeDropdown, useActiveOrganization, useOrgSlug } from '../../contexts/auth';
import { canAccessAdmin } from '../../lib/admin-gate';
import { CreateInterviewDialog } from '../../contexts/interviews';
import { HeaderBreadcrumbsContainer } from '../../containers/HeaderBreadcrumbs';
import {
  CommandPalette,
  CommandPaletteProvider,
  useCommandPalette,
} from '../../containers/CommandPalette';
import { SearchPill } from '../../components/search-pill';
import { candidatesHomeSearch, interviewsHomeSearch } from '../../router/tree';

const PALETTE_PROMPTS = [
  'Invite a candidate',
  'View session results',
  'Find a candidate',
  'Create a template',
  'Jump to settings',
] as const;

export function AppLayout() {
  return (
    <SidebarProvider
      defaultOpen
      className="h-full"
      style={{ '--sidebar-width': '20rem' } as CSSProperties}
    >
      <CommandPaletteProvider>
        <AppShell />
        <CommandPalette />
      </CommandPaletteProvider>
    </SidebarProvider>
  );
}

function AppShell() {
  const { organization } = useActiveOrganization();
  const orgSlug = useOrgSlug();
  const { data: me } = useGetMe();
  const canAdmin = Boolean(me && canAccessAdmin(me));
  const createDialog = useDialog(CreateInterviewDialog);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { state, isMobile } = useSidebar();
  const { setOpen: setPaletteOpen } = useCommandPalette();

  const isOrgSubpathActive = (suffix: string) =>
    new RegExp(`^/orgs/[^/]+/${suffix}(/|$)`).test(pathname);

  const isSessionsActive = isOrgSubpathActive('sessions');
  const isTemplatesActive = isOrgSubpathActive('templates');
  const isRubricsActive = isOrgSubpathActive('rubrics');
  const isExtractionsActive = isOrgSubpathActive('extractions');
  const isCandidatesActive = isOrgSubpathActive('candidates');
  const isTeamActive = isOrgSubpathActive('settings/team');
  const isApiKeysActive = isOrgSubpathActive('settings/api-keys');
  const isBrandingActive = isOrgSubpathActive('settings/branding');
  const isWebhooksActive = isOrgSubpathActive('settings/webhooks');
  const isDomainsActive = isOrgSubpathActive('settings/domains');

  const showSearchInSidebar = !isMobile && state === 'expanded';
  const openPalette = () => setPaletteOpen(true);

  // Some routes (e.g. the two-column template editor) opt into a wider content
  // column via `staticData.wide`; everything else keeps the comfortable default.
  const isWide = useRouterState({
    select: (s) => s.matches.some((m) => m.staticData?.wide === true),
  });

  return (
    <>
      <Sidebar collapsible="icon" variant="floating" side="left">
        <SidebarHeader className="gap-2 group-data-[collapsible=icon]:hidden">
          {showSearchInSidebar && <SearchPill prompts={PALETTE_PROMPTS} onClick={openPalette} />}
        </SidebarHeader>

        <SidebarSeparator className="bg-foreground/10 group-data-[collapsible=icon]:hidden" />

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Interviews</SidebarGroupLabel>
            {organization && (
              <SidebarGroupAction
                title="New interview"
                aria-label="New interview"
                onClick={() => createDialog.open({ orgId: organization.id })}
              >
                <Plus strokeWidth={1.8} aria-hidden />
                <span className="sr-only">New interview</span>
              </SidebarGroupAction>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Sessions" isActive={isSessionsActive}>
                    <Link
                      to="/orgs/$orgSlug/sessions"
                      params={{ orgSlug }}
                      search={interviewsHomeSearch}
                    >
                      <AudioLines strokeWidth={1.6} aria-hidden />
                      <span>Sessions</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Candidates" isActive={isCandidatesActive}>
                    <Link
                      to="/orgs/$orgSlug/candidates"
                      params={{ orgSlug }}
                      search={candidatesHomeSearch}
                    >
                      <Users strokeWidth={1.6} aria-hidden />
                      <span>Candidates</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Templates" isActive={isTemplatesActive}>
                    <Link to="/orgs/$orgSlug/templates" params={{ orgSlug }}>
                      <LayoutTemplate strokeWidth={1.6} aria-hidden />
                      <span>Templates</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Rubrics" isActive={isRubricsActive}>
                    <Link to="/orgs/$orgSlug/rubrics" params={{ orgSlug }}>
                      <Target strokeWidth={1.6} aria-hidden />
                      <span>Rubrics</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Extractions" isActive={isExtractionsActive}>
                    <Link to="/orgs/$orgSlug/extractions" params={{ orgSlug }}>
                      <Braces strokeWidth={1.6} aria-hidden />
                      <span>Extractions</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel>Settings</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Team" isActive={isTeamActive}>
                    <Link to="/orgs/$orgSlug/settings/team" params={{ orgSlug }}>
                      <UsersRound strokeWidth={1.6} aria-hidden />
                      <span>Team</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="API keys" isActive={isApiKeysActive}>
                    <Link to="/orgs/$orgSlug/settings/api-keys" params={{ orgSlug }}>
                      <KeyRound strokeWidth={1.6} aria-hidden />
                      <span>API keys</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Branding" isActive={isBrandingActive}>
                    <Link to="/orgs/$orgSlug/settings/branding" params={{ orgSlug }}>
                      <Image strokeWidth={1.6} aria-hidden />
                      <span>Branding</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Webhooks" isActive={isWebhooksActive}>
                    <Link to="/orgs/$orgSlug/settings/webhooks" params={{ orgSlug }}>
                      <Webhook strokeWidth={1.6} aria-hidden />
                      <span>Webhooks</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Custom domains" isActive={isDomainsActive}>
                    <Link to="/orgs/$orgSlug/settings/domains" params={{ orgSlug }}>
                      <Globe strokeWidth={1.6} aria-hidden />
                      <span>Custom domains</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {/* Staff-only: entry point to the /admin subtree (its own chrome). */}
                {canAdmin && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Admin">
                      <Link to="/admin">
                        <Shield strokeWidth={1.6} aria-hidden />
                        <span>Admin</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="gap-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <ThemeToggleMenuItem />
            </SidebarMenuItem>
          </SidebarMenu>
          <SidebarSeparator className="bg-foreground/10 group-data-[collapsible=icon]:hidden" />
          <SidebarMenu>
            <SidebarMenuItem>
              <MeDropdown />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="min-h-0">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <header className="sticky top-0 z-10 bg-transparent">
            <div className="flex h-14 w-full items-center gap-3 px-3">
              <SidebarTrigger className="shrink-0 text-muted-foreground hover:text-foreground" />
              <HeaderBreadcrumbsContainer />
              {!showSearchInSidebar && (
                <div className="ml-auto flex w-full min-w-0 max-w-[180px] shrink justify-end sm:max-w-xs md:max-w-md">
                  <SearchPill prompts={PALETTE_PROMPTS} onClick={openPalette} />
                </div>
              )}
            </div>
          </header>

          <div
            className={cn(
              'mx-auto flex min-h-0 w-full flex-1 flex-col px-4 pb-12 pt-4 md:px-10 md:pt-6',
              isWide ? 'max-w-[90rem]' : 'max-w-5xl'
            )}
          >
            <Outlet />
          </div>
        </div>
      </SidebarInset>
    </>
  );
}

function ThemeToggleMenuItem() {
  const { theme, setTheme } = useTheme();
  const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
  const label = theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System';
  const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;

  return (
    <SidebarMenuButton
      type="button"
      tooltip={`Theme: ${label}`}
      aria-label={`Theme: ${label}. Click to switch.`}
      onClick={() => setTheme(next)}
    >
      <Icon strokeWidth={1.6} aria-hidden />
      <span>Theme: {label}</span>
    </SidebarMenuButton>
  );
}
